import fontkit from "@pdf-lib/fontkit";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export async function makePDFCertificate(
  inputPdfPath,
  userName,
  title = "",
  type = ""
) {
  const pdfBytes = await fetch(inputPdfPath).then((res) => res.arrayBuffer());

  const pdfDoc = await PDFDocument.load(pdfBytes);
  pdfDoc.registerFontkit(fontkit);

  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  pdfDoc.setTitle(title);
  const fontBytes = await fetch("/fonts/OpenSans-Bold.ttf").then((res) =>
    res.arrayBuffer()
  );
  const customFont = await pdfDoc.embedFont(fontBytes);

  const helveticaBold = customFont;
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const { width, height } = firstPage.getSize();
  const fontSize = 31;
  const maxWidth = 300;
  const lineHeight = fontSize + 5;

  function splitTextIntoLines(text, font, size, maxWidth) {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = font.widthOfTextAtSize(testLine, size);
      if (textWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) lines.push(currentLine);
    return lines;
  }

  const nameLines = splitTextIntoLines(
    userName,
    helveticaBold,
    fontSize,
    maxWidth
  );

  let baseOffset;
  switch (type) {
    case "rar":
      baseOffset = 160;
      break;
    case "or":
      baseOffset = 280;
      break;
    case "tp":
      baseOffset = 300;
      break;
    case "tt6":
      baseOffset = 390;
      break;
    case "certificate":
      baseOffset = 330;
      break;
    default:
      baseOffset = 330;
  }
  let offset;
  if (nameLines.length === 2) {
    offset = baseOffset - 15;
  } else {
    offset = baseOffset;
  }
  const startY = height - offset;

  nameLines.forEach((line, index) => {
    const lineWidth = helveticaBold.widthOfTextAtSize(line, fontSize);
    const lineX = (width - lineWidth) / 2;

    firstPage.drawText(line, {
      x: lineX,
      y: startY - index * lineHeight,
      size: fontSize,
      color: rgb(80 / 255, 79 / 255, 79 / 255),
      font: helveticaBold,
    });
  });

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}.${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}.${today.getFullYear()}.`;
  let dateOffset;
  switch (type) {
    case "rar":
      dateOffset = 460;
      break;
    case "or":
      dateOffset = 600;
      break;
    case "tp":
      dateOffset = 600;
      break;
    case "tt6":
      dateOffset = 700;
      break;
    case "certificate":
      dateOffset = 760;
      break;
    default:
      dateOffset = 600;
  }

  firstPage.drawText(`${formattedDate}`, {
    x: (width - 60) / 2,
    y: height - dateOffset,
    size: 12,
    color: rgb(80 / 255, 79 / 255, 79 / 255),
    font: helveticaFont,
  });
  const modifiedPdfBytes = await pdfDoc.save();
  return new Blob([modifiedPdfBytes], { type: "application/pdf" });
}

export async function makeMultiPagePDFCertificate(
  inputPdfPath,
  namesArray,
  title = "",
  type = ""
) {
  const pdfBytes = await fetch(inputPdfPath).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const templatePdf = await PDFDocument.load(pdfBytes);
  const fontBytes = await fetch("/fonts/OpenSans-Bold.ttf").then((res) =>
    res.arrayBuffer()
  );

  const customFont = await pdfDoc.embedFont(fontBytes);
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const fontSize = 31;
  const maxWidth = 700;
  const lineHeight = fontSize + 5;

  function splitTextIntoLines(text, font, size, maxWidth) {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = font.widthOfTextAtSize(testLine, size);
      if (textWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) lines.push(currentLine);
    return lines;
  }

  for (const userName of namesArray) {
    const [templateCopy] = await pdfDoc.copyPages(templatePdf, [0]);
    const page = pdfDoc.addPage(templateCopy);
    const { width, height } = page.getSize();

    const nameLines = splitTextIntoLines(
      userName,
      customFont,
      fontSize,
      maxWidth
    );
    let baseOffset;
    switch (type) {
      case "rar":
        baseOffset = 160;
        break;
      case "or":
        baseOffset = 280;
        break;
      case "tp":
        baseOffset = 300;
        break;
      case "tt6":
        baseOffset = 390;
        break;
      case "certificate":
        baseOffset = 330;
        break;
      default:
        baseOffset = 330;
    }
    let offset;
    if (nameLines.length === 2) {
      offset = baseOffset - 15;
    } else {
      offset = baseOffset;
    }
    const startY = height - offset;

    nameLines.forEach((line, index) => {
      const lineWidth = customFont.widthOfTextAtSize(line, fontSize);
      const lineX = (width - lineWidth) / 2;

      page.drawText(line, {
        x: lineX,
        y: startY - index * lineHeight,
        size: fontSize,
        color: rgb(80 / 255, 79 / 255, 79 / 255),
        font: customFont,
      });
    });

    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, "0")}.${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${today.getFullYear()}.`;
    let dateOffset;
    switch (type) {
      case "rar":
        dateOffset = 460;
        break;
      case "or":
        dateOffset = 600;
        break;
      case "tp":
        dateOffset = 600;
        break;
      case "tt6":
        dateOffset = 700;
        break;
      case "certificate":
        dateOffset = 760;
        break;
      default:
        dateOffset = 600;
    }

    page.drawText(`${formattedDate}`, {
      x: (width - 60) / 2,
      y: height - dateOffset,
      size: 12,
      color: rgb(80 / 255, 79 / 255, 79 / 255),
      font: helveticaFont,
    });
  }

  pdfDoc.setTitle(title);

  const modifiedPdfBytes = await pdfDoc.save();
  const pdfBlob = new Blob([modifiedPdfBytes], { type: "application/pdf" });

  // Auto-download the PDF
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "certificates.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
